import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CauseListEntry, Division, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const DIVISIONS: Division[] = ['SUPREME_CIVIL', 'COMMERCIAL', 'FAMILY', 'MAGISTRATES', 'EMPLOYMENT_TRIBUNAL', 'APPEALS'];
const HEARING_ROLES = ['COURT_CLERK', 'REGISTRAR_COURT', 'JUDGE', 'TRIBUNAL_MEMBER'];

function todayISODate(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * GET /api/v1/judiciary/cause-lists — scheduled/adjourned hearings for the
 * next 7 days by default (or one exact ?date=). Sealed family/juvenile
 * matters are reduced server-side to courtroom/time/division only (the
 * case number itself never leaks) — this page just renders whatever the
 * API returns, no client-side redaction logic of its own.
 */
export function CauseLists() {
  const { persona } = useAuth();
  const canPublish = persona?.roles.some((r) => HEARING_ROLES.includes(r)) ?? false;
  const [division, setDivision] = useState<Division | ''>('');
  const [date, setDate] = useState('');
  const [showPublish, setShowPublish] = useState(false);
  const [publishResult, setPublishResult] = useState<{ published: number; noticeRef: string } | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishDivision, setPublishDivision] = useState<Division>('SUPREME_CIVIL');
  const [publishDate, setPublishDate] = useState(todayISODate());

  const params = new URLSearchParams();
  if (division) params.set('division', division);
  if (date) params.set('date', date);
  const listQ = useApiQuery<{ items: CauseListEntry[] }>(`/api/v1/judiciary/cause-lists${params.toString() ? `?${params}` : ''}`);

  const publish = async () => {
    setPublishBusy(true);
    setPublishError(null);
    try {
      const res = await api.post<{ published: number; noticeRef: string }>('/api/v1/judiciary/cause-lists/publish', {
        division: publishDivision,
        date: publishDate,
      });
      setPublishResult(res);
      setShowPublish(false);
      listQ.reload();
    } catch (e) {
      setPublishError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Publish failed');
    } finally {
      setPublishBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Cause Lists"
        subtitle="Scheduled and adjourned hearings — next 7 days by default, or one exact date."
        actions={
          canPublish ? (
            <button className="btn-primary" onClick={() => setShowPublish((s) => !s)}>
              {showPublish ? 'Close' : '+ Publish to Gazette'}
            </button>
          ) : undefined
        }
      />

      {publishResult ? (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800">
          Published {publishResult.published} entr{publishResult.published === 1 ? 'y' : 'ies'} — notice ref{' '}
          <code>{publishResult.noticeRef}</code>
        </div>
      ) : null}

      {showPublish ? (
        <div className="card mb-5 p-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Publish one division's cause list for one day — every unpublished SCHEDULED hearing that day goes to the Gazette.
          </div>
          {publishError ? <ErrorNote message={publishError} /> : null}
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Division" className="!mb-0">
              <select className="field-input !w-auto" value={publishDivision} onChange={(e) => setPublishDivision(e.target.value as Division)}>
                {DIVISIONS.map((d) => (
                  <option key={d} value={d}>
                    {d.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Date" className="!mb-0">
              <input type="date" className="field-input" value={publishDate} onChange={(e) => setPublishDate(e.target.value)} />
            </Field>
            <button className="btn-primary" onClick={publish} disabled={publishBusy}>
              {publishBusy ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Division" className="!mb-0">
          <select className="field-input !w-auto" value={division} onChange={(e) => setDivision(e.target.value as Division | '')}>
            <option value="">All</option>
            {DIVISIONS.map((d) => (
              <option key={d} value={d}>
                {d.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Exact date (optional)" className="!mb-0">
          <input type="date" className="field-input" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No hearings in this window" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">When</th>
                <th className="table-head">Division</th>
                <th className="table-head">Courtroom</th>
                <th className="table-head">Purpose</th>
                <th className="table-head">Case</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((h) => (
                <tr key={h.id} className="hover:bg-slate-50">
                  <td className="table-cell text-xs">{formatDateTime(h.scheduledAt)}</td>
                  <td className="table-cell text-xs">{h.division.replaceAll('_', ' ')}</td>
                  <td className="table-cell">{h.courtroom}</td>
                  <td className="table-cell text-xs">{h.purpose.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{h.sealed ? '🔒 sealed' : (h.caseNumber ?? '—')}</td>
                  <td className="table-cell">
                    <StatusBadge status={h.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
